const DAY_MS = 86_400_000;

const madridDateFormatter = new Intl.DateTimeFormat("en-CA", {
    timeZone: "Europe/Madrid",
    year: "numeric",
    month: "2-digit",
    day: "2-digit",
});

const madridPartsFormatter = new Intl.DateTimeFormat("en-CA", {
    timeZone: "Europe/Madrid",
    hourCycle: "h23",
    year: "numeric",
    month: "2-digit",
    day: "2-digit",
    hour: "2-digit",
    minute: "2-digit",
    second: "2-digit",
});

const dayLabelFormatter = new Intl.DateTimeFormat("es-ES", {
    timeZone: "UTC",
    weekday: "short",
    day: "numeric",
    month: "short",
    year: "numeric",
});

const shortDayFormatter = new Intl.DateTimeFormat("es-ES", { timeZone: "UTC", day: "numeric", month: "short" });

const monthFormatter = new Intl.DateTimeFormat("es-ES", { timeZone: "UTC", month: "long" });

const partNumber = (parts: Intl.DateTimeFormatPart[], type: Intl.DateTimeFormatPartTypes): number => {
    const part = parts.find((entry) => entry.type === type);
    return part === undefined ? 0 : Number(part.value);
};

/** How far Madrid's wall clock is ahead of UTC at that instant (DST-aware). */
const madridOffsetMs = (at: number): number => {
    const parts = madridPartsFormatter.formatToParts(new Date(at));
    const wall = Date.UTC(
        partNumber(parts, "year"),
        partNumber(parts, "month") - 1,
        partNumber(parts, "day"),
        partNumber(parts, "hour"),
        partNumber(parts, "minute"),
        partNumber(parts, "second"),
    );
    return wall - Math.floor(at / 1_000) * 1_000;
};

/**
 * Calendar helpers for Daily. Every date is a Madrid calendar day written as
 * YYYY-MM-DD; arithmetic happens at noon UTC so a DST change can never skip or
 * repeat a day.
 */
export const dailyDates = {
    isIsoDate: (value: string | null): value is string =>
        value !== null && /^\d{4}-\d{2}-\d{2}$/.test(value) && !Number.isNaN(Date.parse(`${value}T12:00:00Z`)),

    /** The Madrid calendar day an instant falls on. */
    madridDateOf: (at: number): string => madridDateFormatter.format(new Date(at)),

    shift: (date: string, days: number): string =>
        new Date(Date.parse(`${date}T12:00:00Z`) + days * DAY_MS).toISOString().slice(0, 10),

    /** The instant a Madrid calendar day starts (00:00 Madrid), in epoch milliseconds. */
    madridStartOf: (date: string): number => {
        const naive = Date.parse(`${date}T00:00:00Z`);
        const first = naive - madridOffsetMs(naive);
        return naive - madridOffsetMs(first);
    },

    dayLabel: (date: string): string => dayLabelFormatter.format(new Date(`${date}T12:00:00Z`)),

    shortLabel: (date: string): string => shortDayFormatter.format(new Date(`${date}T12:00:00Z`)),

    monthName: (date: string): string => monthFormatter.format(new Date(`${date}T12:00:00Z`)),
};
